/**
 * AppShell - Entry point for the authenticated application
 *
 * Simplified component that handles only:
 * - Provider wrapping (Theme, Loading, DocumentSelection, UIState)
 * - Authentication gate (show AuthPanel if not logged in)
 * - Delegation to AuthenticatedApp for logged-in users
 *
 * Refactored from 811 lines to ~80 lines following Clean Code principles.
 *
 * Original code backed up in: AppShell.tsx.backup_original
 */

import React from 'react';
import { Loader2 } from 'lucide-react';
import { useTheme } from './contexts/ThemeContext';
import { LoadingProvider } from './contexts/LoadingContext';
import { DocumentSelectionProvider } from './contexts/DocumentSelectionContext';
import { UIStateProvider } from './contexts/UIStateContext';
import { useAuthentication } from './hooks/useAuthentication';
import AuthPanel from './components/AuthPanel';
import { AuthenticatedApp } from './components/AuthenticatedApp';
import logger from './utils/simpleLogger';

/**
 * AppShell - Main application shell
 *
 * Responsibilities:
 * - Provide global contexts
 * - Handle authentication state
 * - Route to appropriate view (AuthPanel or AuthenticatedApp)
 */
function AppShell() {
  const { theme } = useTheme();
  const auth = useAuthentication();

  // Show loading state while checking authentication
  if (auth.isLoading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{
          height: '100dvh',
          backgroundColor: 'var(--nb-bg)',
          color: 'var(--nb-text)',
        }}
      >
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin" />
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Show authentication panel if not logged in
  if (!auth.currentUser) {
    logger.info('AppShell: User not authenticated, showing AuthPanel');
    return (
      <AuthPanel
        onAuthSuccess={(user, token) => {
          // AuthPanel handles token storage internally, but we need to trigger checkAuth
          // to update the auth state. Since useAuthentication already manages token,
          // we just need to ensure the state is refreshed.
          auth.checkAuth();
        }}
      />
    );
  }

  // Render authenticated app with all providers
  logger.info('AppShell: User authenticated, rendering AuthenticatedApp');
  return (
    <LoadingProvider>
      <DocumentSelectionProvider>
        <UIStateProvider>
          <AuthenticatedApp
            currentUser={auth.currentUser}
            onLogout={auth.logout}
            onRefreshUser={auth.checkAuth}
          />
        </UIStateProvider>
      </DocumentSelectionProvider>
    </LoadingProvider>
  );
}

export default AppShell;
